import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Compass, Boxes, Ship, Handshake } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";

const IMG = {
  containers: "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?auto=format&fit=crop&w=1400&q=80",
  circuit: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=80",
  cargo: "https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?auto=format&fit=crop&w=1200&q=80",
  drone: "https://images.unsplash.com/photo-1487219116710-23ffcb172b2b?auto=format&fit=crop&w=1200&q=80",
  network: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=80",
};

const services = [
  {
    n: "01",
    icon: Handshake,
    title: "Consulting",
    body: "Strategic guidance on the intricacies of markets and geographic areas to help small to medium sized companies expand and compete in the international marketplace. De-risking every decision in your global operations.",
  },
  {
    n: "02",
    icon: Compass,
    title: "International Sourcing",
    body: "We identify, vet, and connect you with reliable suppliers outside the U.S. for the exact components your product demands.",
  },
  {
    n: "03",
    icon: Ship,
    title: "Logistics Coordination",
    body: "End-to-end coordination of freight, customs, and delivery timelines so parts arrive predictably, wherever they originate.",
  },
  {
    n: "04",
    icon: Boxes,
    title: "Purchasing & Shipping",
    body: "Third-party purchasing and shipping support that handles the transactional friction of buying and moving goods across borders.",
  },
];

const manifesto = [
  {
    n: "01",
    title: "Operations across borders is hard.",
    body: "Local regulations, business norms, language, and time zones can turn minor issues into major problems. We remove that friction.",
  },
  {
    n: "02",
    title: "Engineers should engineer.",
    body: "Your team's time belongs on the product — not chasing suppliers overseas. We handle coordination while you keep building.",
  },
  {
    n: "03",
    title: "Coordination is the product.",
    body: "From first inquiry to final delivery, we act as a single point of accountability across purchasing, logistics, and shipping.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden pb-16 pt-32 md:pb-24">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 -z-10">
          <img src={IMG.containers} alt="Aerial view of an international shipping container yard" className="h-full w-full object-cover opacity-[0.55] grayscale" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/65 to-background" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/40 to-transparent" />
        </motion.div>

        <div className="mx-auto w-full max-w-[1600px] px-6 md:px-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-primary"
          >
            <span className="h-px w-10 bg-primary" /> Global Operation, Simplified
          </motion.p>

          <h1 className="font-heading text-[13vw] font-black uppercase leading-[0.85] tracking-tighter md:text-[8.5vw]">
            <MaskedLines lines={["International", "Services"]} />
            <span className="block overflow-hidden">
              <motion.span
                className="block text-primary"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.24, ease: [0.76, 0, 0.24, 1] }}
              >
                Made Simple
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-10 flex flex-col items-start gap-8 md:flex-row md:items-end md:justify-between"
          >
            <p className="max-w-xl font-mono text-sm leading-relaxed text-muted-foreground md:text-base">
              Prescience Services helps see through the difficulties associated with
              international operations. Whether it&apos;s understanding the intricacies of foreign markets
              for potential expansion, or finding, purchasing and shipping components from outside your
              home country, Prescience is available to provide clarity and outstanding value for our clients.
            </p>
            <Link
              to="/contact"
              data-testid="hero-cta-button"
              className="group inline-flex shrink-0 items-center gap-3 bg-primary px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-primary-foreground transition-transform duration-300 hover:-translate-y-1"
            >
              Start a project
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="relative z-10 mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
          <Reveal className="md:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">[ 001 — What we do ]</p>
            <h2 className="mt-6 font-heading text-4xl font-bold uppercase leading-none tracking-tight md:text-5xl">
              Four ways we move parts across the map
            </h2>
            <p className="mt-6 font-mono text-sm leading-relaxed text-muted-foreground">
              One partner for sourcing, consulting, logistics, and purchasing — built for networking gear,
              drones, electronics, and general engineering hardware.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 gap-6 md:col-span-8 md:grid-cols-2">
            {services.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div
                  data-testid={`service-card-${s.n}`}
                  className="group h-full border border-white/10 bg-white/[0.03] p-8 transition-colors duration-500 hover:border-primary hover:bg-white/[0.06]"
                >
                  <div className="flex items-center justify-between">
                    <s.icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
                    <span className="font-mono text-xs text-white/40">{s.n}</span>
                  </div>
                  <h3 className="mt-8 font-heading text-2xl font-bold tracking-tight">{s.title}</h3>
                  <p className="mt-4 font-mono text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section data-testid="manifesto-section" className="relative z-10 border-t border-white/10 bg-[hsl(155,38%,4%)]">
        <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">[ 002 — Why it matters ]</p>
          </Reveal>
          <div className="mt-16 space-y-28">
            {manifesto.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.05}>
                <div className="relative grid grid-cols-1 items-center gap-8 md:grid-cols-12">
                  <span className="pointer-events-none absolute -top-24 left-0 -z-0 select-none font-heading text-[13rem] font-black leading-none text-white/[0.04] md:text-[18rem]">
                    {m.n}
                  </span>
                  <div className="relative z-10 md:col-span-7 md:col-start-2">
                    <h3 className="font-heading text-3xl font-bold leading-tight tracking-tight md:text-5xl">
                      {m.title}
                    </h3>
                  </div>
                  <div className="relative z-10 md:col-span-3 md:col-start-9">
                    <p className="font-mono text-sm leading-relaxed text-muted-foreground">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY / TRUST with imagery */}
      <section data-testid="why-section" className="relative z-10 mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
          <Reveal className="media-frame relative overflow-hidden border border-white/20 md:col-span-5 md:row-span-2">
            <img src={IMG.circuit} alt="Macro photo of a circuit board component" className="h-full min-h-[280px] w-full object-cover" />
          </Reveal>

          <Reveal className="md:col-span-7" delay={0.1}>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">[ 003 — Why Prescience ]</p>
            <h2 className="mt-6 font-heading text-4xl font-bold uppercase leading-none tracking-tight md:text-6xl">
              A single point of accountability
            </h2>
            <p className="mt-6 max-w-2xl font-mono text-sm leading-relaxed text-muted-foreground md:text-base">
              We speak the language of engineering and the language of global trade. That lets us translate a
              spec sheet into a sourced, purchased, and delivered part — without your team living in supplier
              inboxes and freight portals.
            </p>
          </Reveal>

          <div className="grid grid-cols-3 gap-6 md:col-span-7">
            {[
              { k: "Worldwide", v: "Supplier reach" },
              { k: "End-to-end", v: "Coordination" },
              { k: "1 partner", v: "Full accountability" },
            ].map((stat, i) => (
              <Reveal key={stat.v} delay={0.15 + i * 0.08}>
                <div className="border-t border-white/15 pt-4">
                  <p className="font-heading text-2xl font-extrabold tracking-tight text-primary md:text-3xl">{stat.k}</p>
                  <p className="mt-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">{stat.v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* sector strip */}
        <Reveal className="mt-24" delay={0.1}>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { img: IMG.drone, label: "Drones & UAV", desc: "Motors, controllers, and airframe hardware." },
              { img: IMG.circuit, label: "Electronics", desc: "Boards, sensors, and specialized components." },
              { img: IMG.network, label: "Networking", desc: "Infrastructure and connectivity hardware." },
            ].map((sec, i) => (
              <div key={sec.label} className="media-frame group relative overflow-hidden border border-white/15">
                <img src={sec.img} alt={sec.label} className="h-64 w-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-6">
                  <p className="font-heading text-xl font-bold uppercase tracking-tight">{sec.label}</p>
                  <p className="mt-1 font-mono text-xs text-white/60">{sec.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
